import { Card, Col, Form, Row } from 'react-bootstrap';

import { PraSection } from '../../../../../../__gqltypes__';
import { PraBoxDraft, PraBoxPoolDraft } from '../utils';
import { PraBoxPoolTable } from './PraBoxPoolTable';

type Props = {
  section: PraSection;
  hasBox: boolean;
  box: PraBoxDraft | null;
  onToggleBox: (enabled: boolean) => void;
  onChangeBox: (box: PraBoxDraft) => void;
};

const parseInteger = (raw: string): number => Math.max(0, Math.floor(Number(raw) || 0));

// Random Box editor (revised design D21–D25): the player sees ONE box, backed by TWO
// admin-configured pools — the milestone (ad/free) box and the Diamond-purchased boxes —
// plus the shared flat priceDiamonds and the per-cycle purchase cap.
export function PraRandomBoxEditor({ section, hasBox, box, onToggleBox, onChangeBox }: Props): JSX.Element {
  const handlePoolChange = (poolKey: 'freePool' | 'purchasePool') => (pool: PraBoxPoolDraft) => {
    if (box) {
      onChangeBox({ ...box, [poolKey]: pool });
    }
  };

  return (
    <Form.Group>
      <Form.Check
        type="switch"
        id={`pra-random-box-${section}`}
        label={
          section === PraSection.FREE
            ? 'The reward is a Random Box (FREE has exactly one entry, so the box is the whole sequence)'
            : 'Last milestone is a Random Box'
        }
        checked={hasBox}
        onChange={(e) => onToggleBox(e.target.checked)}
      />
      {hasBox && box && (
        <Card className="mt-2">
          <Card.Header>
            <strong>Random Box</strong>{' '}
            <span className="text-muted small">
              Rolled server-side at claim/purchase: items are drawn weighted WITHOUT replacement (no duplicate
              item types per opening), amounts roll uniformly within each item&apos;s min–max range.
            </span>
          </Card.Header>
          <Card.Body>
            <PraBoxPoolTable
              poolId={`pra-${section}-free-pool`}
              title="Milestone box (ad/free)"
              hint="backs the box milestone claim (ad for basic players, direct for ad-free)"
              pool={box.freePool}
              onChange={handlePoolChange('freePool')}
            />
            <PraBoxPoolTable
              poolId={`pra-${section}-purchase-pool`}
              title="Purchased boxes (Diamonds)"
              hint="every extra Diamond purchase draws from this same pool"
              pool={box.purchasePool}
              onChange={handlePoolChange('purchasePool')}
            />
            <Row>
              <Col xs={12} md={4}>
                <Form.Label className="small text-muted mb-0" htmlFor={`pra-${section}-box-price`}>
                  Box price (Diamonds, flat per purchase — &gt;= 1)
                </Form.Label>
                <Form.Control
                  id={`pra-${section}-box-price`}
                  type="number"
                  min={1}
                  value={box.priceDiamonds}
                  onChange={(e) => onChangeBox({ ...box, priceDiamonds: parseInteger(e.target.value) })}
                />
              </Col>
              <Col xs={12} md={4}>
                <Form.Label className="small text-muted mb-0" htmlFor={`pra-${section}-box-cap`}>
                  Purchase cap per cycle (0 = purchases disabled)
                </Form.Label>
                <Form.Control
                  id={`pra-${section}-box-cap`}
                  type="number"
                  min={0}
                  value={box.purchaseCapPerCycle}
                  onChange={(e) => onChangeBox({ ...box, purchaseCapPerCycle: parseInteger(e.target.value) })}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
    </Form.Group>
  );
}
